import React, { useEffect, useState } from "react";
import styles from "./PaginationBlock.module.scss";
import classNames from "classnames";
import { Icon } from "SiteComponents";
import useWindowSize from "src/utils/useWindowSize";

export interface PaginationBlockProps {
  currentPage?: number;
  itemLabel?: (num: number) => string;
  nextLabel?: string;
  onChange?: (page: number) => void;
  previousLabel?: string;
  totalPages?: number;
  navAriaLabel?: string;
}

const PaginationBlock: React.FC<PaginationBlockProps> = ({
  currentPage = 1,
  itemLabel = num => `Side ${num}`,
  nextLabel = "Neste",
  onChange = () => {},
  previousLabel = "Forrige",
  totalPages = 1,
  navAriaLabel = "Paginering",
}) => {
  const [maxPageButtons, setMaxPageButtons] = useState(4);
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onChange(page);
    }
    scrollToTop();
  };

  const isDesktop = useWindowSize().width > 768;

  const scrollToTop = () => {
    window.scrollTo({ top: 500, behavior: "smooth" });
  };

  useEffect(() => {
    setMaxPageButtons(isDesktop ? 5 : 4);
  }, [isDesktop]);

  const { pages, showStartEllipsis, showEndEllipsis } = (() => {
    const showStartEllipsis = totalPages > maxPageButtons && currentPage > 3;
    const showEndEllipsis =
      totalPages > maxPageButtons && currentPage < totalPages - 3;

    // Adjust startPage to correctly handle when you're beyond page 3
    let startPage = Math.max(currentPage - 1, 1);

    // // Ensure we don't exceed totalPages and still show the last page
    // if (totalPages - startPage + 1 < maxPageButtons) {
    //   startPage = Math.max(totalPages - maxPageButtons, 1);
    // }
    if (!showEndEllipsis) {
      startPage = Math.max(totalPages - maxPageButtons + 1, 1);
    }

    // Calculate the number of pages to show
    let shownPages = maxPageButtons;
    if (showStartEllipsis) shownPages--;
    if (showEndEllipsis) shownPages--;

    // Now generate the pages array, ensuring we don't exceed totalPages
    const pages = Array.from(
      { length: shownPages },
      (_, idx) => startPage + idx
    ).filter(page => page <= totalPages); // Ensure no pages beyond totalPages

    return { pages, showStartEllipsis, showEndEllipsis };
  })();

  if (!currentPage) return null;
  if (totalPages === 1) return null;
  return (
    <nav aria-label={navAriaLabel} className={styles.paginationBlock}>
      <button
        disabled={currentPage === 1}
        className={classNames(styles.prevButton, {
          [styles.hide]: currentPage === 1,
        })}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        <Icon name="chevron-left" />
        <span className={styles.navButtonLabel}> {previousLabel}</span>
      </button>
      {showStartEllipsis && (
        <>
          <button
            aria-label={itemLabel(1)}
            className={classNames(styles.pageButton, {
              [styles.current]: 1 === currentPage,
            })}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>
          <span className={styles.ellipsis}>...</span>
        </>
      )}
      {pages.map(page => (
        <button
          key={page}
          aria-label={itemLabel(page)}
          className={classNames(styles.pageButton, {
            [styles.current]: page == currentPage && totalPages !== 1,
          })}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </button>
      ))}
      {showEndEllipsis && (
        <>
          <span className={styles.ellipsis}>...</span>
          <button
            aria-label={itemLabel(totalPages)}
            className={classNames(styles.pageButton, {
              [styles.current]: totalPages === currentPage,
            })}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </>
      )}
      {currentPage < totalPages && (
        <button
          className={classNames(styles.nextButton, {
            [styles.hide]: currentPage === totalPages,
          })}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          <span className={styles.navButtonLabel}>{nextLabel}</span>
          <Icon name="chevron-right" />
        </button>
      )}
    </nav>
  );
};

export default PaginationBlock;
